/**
 * @fileoverview
 * Footer section for the homepage.
 *
 * This component renders the site footer with:
 * - AIDECO logo
 * - Tagline "AI-powered architecture rendering for teams who need stunning visuals fast."
 * - Contact email and mailing address
 * - Social media links (LinkedIn, Instagram, Facebook)
 * - Footer navigation links (Terms, Privacy, About, Features, Pricing, Blog, Contact)
 * - Copyright with address
 *
 * Fulfills: VAL-HOME-025
 */

"use client";

import Image from "next/image";
import Link from "next/link";
import { Trans, useLingui } from "@lingui/react/macro";
import { siteConfig } from "@/config/site";

/**
 * Social media icon components using Lucide-style SVG paths
 */
const LinkedInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const InstagramIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const FacebookIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

/**
 * FooterSection renders the site footer.
 *
 * @returns JSX.Element
 */
export function FooterSection() {
  const { t } = useLingui();
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { href: "/terms", label: t`Terms of Service` },
    { href: "/privacy", label: t`Privacy Policy` },
    { href: "/about", label: t`About` },
    { href: "/feature", label: t`Features` },
    { href: "/#pricing", label: t`Pricing` },
    { href: "/blog", label: t`Blog` },
    { href: "/contact", label: t`Contact` },
  ];

  const socialLinks = [
    {
      label: t`Follow Aideco on LinkedIn`,
      href: siteConfig.social[0]?.href ?? "https://www.linkedin.com/company/aidecoai",
      icon: LinkedInIcon,
    },
    {
      label: t`Follow Aideco on Instagram`,
      href: siteConfig.social[1]?.href ?? "https://www.instagram.com/aideco_ai/",
      icon: InstagramIcon,
    },
    {
      label: t`Follow Aideco on Facebook`,
      href: siteConfig.social[2]?.href ?? "https://www.facebook.com/profile.php?id=61567178475837",
      icon: FacebookIcon,
    },
  ];

  return (
    <footer
      role="contentinfo"
      aria-label={t`Site footer`}
      className="relative w-full bg-black"
    >
      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8 lg:py-36">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column: Logo, Tagline, Email, Address, Social */}
          <div className="flex flex-col items-center gap-10 text-center lg:items-start lg:text-left">
            {/* Logo */}
            <Link href="/" className="inline-block">
              <Image
                src="/logos/Gemini_Generated_Image_icqk2micqk2micqk-optimized-removebg-preview.png"
                alt="Aideco logo"
                width={160}
                height={160}
                className="h-auto w-40"
                priority={false}
              />
            </Link>

            {/* Tagline */}
            <p className="max-w-sm text-sm leading-relaxed text-gray-300">
              <Trans>AI-powered architecture rendering for teams who need stunning visuals fast.</Trans>
            </p>

            {/* Email block with divider rules */}
            <div className="w-full max-w-md">
              <hr className="border-gray-700" />
              <a
                href={`mailto:${siteConfig.contact.generalEmail}?subject=mail`}
                className="my-6 block text-3xl font-light tracking-tight text-gray-200 transition-colors hover:text-white sm:text-4xl"
              >
                {siteConfig.contact.generalEmail}
              </a>
              <hr className="border-gray-700" />
            </div>

            {/* Address */}
            <p className="text-sm leading-relaxed text-gray-400">{siteConfig.mailingAddressText}</p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-gray-400"><Trans>Follow us.</Trans></span>
              <div className="flex items-center gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-600 text-gray-300 transition-all hover:border-white hover:text-white"
                  >
                    <social.icon />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Decorative Image (hidden on mobile) */}
          <div className="hidden items-start justify-end lg:flex">
            <Image
              src="/images/marketing/6865892de74cf3fbab1d7325_bg.webp"
              alt={t`Architectural visualization`}
              width={940}
              height={600}
              className="h-auto w-full object-cover"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar: Copyright and Navigation */}
      <div className="border-t border-gray-800">
        <div className="mx-auto max-w-7xl px-6 py-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            {/* Copyright and Address — separate lines */}
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                © {currentYear} Aideco. <Trans>All rights reserved.</Trans>
              </p>
              <p className="text-sm text-gray-400">{siteConfig.mailingAddressText}</p>
              <p className="text-sm text-gray-400">{siteConfig.legalEntityNotice}</p>
            </div>

            {/* Footer Links */}
            <nav aria-label={t`Footer navigation`} className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
              {footerLinks.map((link, index) => (
                <span key={link.href} className="flex items-center">
                  <Link
                    href={link.href}
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                  {index < footerLinks.length - 1 && (
                    <span className="mx-2 text-gray-500">·</span>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
